import { useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { cn, formatMoney } from '@/lib/utils'
import { emptyProductFilters, type ProductFilterState } from './filter-state'
import type { ProductFacets } from './queries'
import { SearchInput } from '@/components/search-input'

function toggle(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

function CheckboxGroup({
  label,
  options,
  selected,
  onToggle,
  collapsible = false,
  defaultOpen = false,
}: {
  label: string
  options: { value: string; label: string; count: number }[]
  selected: string[]
  onToggle: (value: string, label: string) => void
  collapsible?: boolean
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen || selected.length > 0)
  if (!options.length) return null
  const list = (
    <fieldset className="min-w-0">
      <legend className={collapsible ? 'sr-only' : 'mb-1.5 text-xs font-medium text-muted-foreground'}>{label}</legend>
      <div className="grid max-h-44 gap-0.5 overflow-y-auto overscroll-contain pr-1">
        {options.map((option) => (
          <label
            className="grid min-h-8 cursor-pointer grid-cols-[16px_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-1 text-sm hover:bg-muted/60"
            key={option.value}
          >
            <input
              checked={selected.includes(option.value)}
              className="size-4 accent-primary"
              onChange={() => onToggle(option.value, option.label)}
              type="checkbox"
            />
            <span className="min-w-0 [overflow-wrap:anywhere]">{option.label}</span>
            <span className="pl-1 text-xs tabular-nums text-muted-foreground">{option.count.toLocaleString()}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
  if (!collapsible) return list
  return (
    <details className="group min-w-0" onToggle={(event) => setOpen(event.currentTarget.open)} open={open}>
      <summary className="flex min-h-8 cursor-pointer list-none items-center justify-between gap-2 text-xs font-medium text-muted-foreground [&::-webkit-details-marker]:hidden">
        <span>{label}</span>
        <span className="flex items-center gap-1.5">
          {selected.length ? <span className="tabular-nums text-foreground">{selected.length}</span> : null}
          <ChevronDown aria-hidden="true" className="size-3.5 transition-transform group-open:rotate-180" />
        </span>
      </summary>
      <div className="pt-1">{list}</div>
    </details>
  )
}

/** Store price range; applied when a field is left or Enter is pressed, not on every keystroke. */
function PriceRange({
  min,
  max,
  onChange,
}: {
  min: string
  max: string
  onChange: (min: string, max: string) => void
}) {
  const [draft, setDraft] = useState({ min, max, applied: `${min}|${max}` })
  // A cleared chip or "Clear filters" resets the fields.
  if (draft.applied !== `${min}|${max}`) setDraft({ min, max, applied: `${min}|${max}` })
  const apply = () => {
    if (draft.min !== min || draft.max !== max) onChange(draft.min.trim(), draft.max.trim())
  }
  const field = (key: 'min' | 'max', label: string) => (
    <label className="min-w-0">
      <span className="sr-only">{label}</span>
      <SearchInput
        className="h-8 px-2 text-sm tabular-nums"
        inputMode="decimal"
        min={0}
        onBlur={apply}
        onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
        onKeyDown={(event) => {
          if (event.key === 'Enter') apply()
        }}
        placeholder={key === 'min' ? 'Min $' : 'Max $'}
        type="number"
        value={draft[key]}
      />
    </label>
  )
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 text-xs font-medium text-muted-foreground">Store price</legend>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5">
        {field('min', 'Minimum store price')}
        <span aria-hidden="true" className="text-xs text-muted-foreground">
          to
        </span>
        {field('max', 'Maximum store price')}
      </div>
    </fieldset>
  )
}

function priceChip(min: string, max: string) {
  const low = min ? formatMoney(Number(min)) : null
  const high = max ? formatMoney(Number(max)) : null
  if (low && high) return `${low} to ${high}`
  return low ? `${low} and up` : high ? `Up to ${high}` : null
}

/** The demo's catalog filters (manufacturer, category, specifications) over the storefront's facets, plus price. */
export function ProductFilters({
  available,
  value,
  onChange,
  className,
}: {
  available?: ProductFacets
  value: ProductFilterState
  onChange: (value: ProductFilterState) => void
  className?: string
}) {
  // Facets only list options for the current results; keep the labels of chosen filters.
  const [labels, setLabels] = useState<ReadonlyMap<string, string>>(new Map())
  const remember = (key: string, label: string) => setLabels((current) => new Map(current).set(key, label))
  const update = (changes: Partial<ProductFilterState>) => onChange({ ...value, ...changes })
  const specLabel = (facet: string) => labels.get(facet) ?? facet.slice(facet.indexOf('=') + 1)
  const active = [
    ...value.manufacturers.map((item) => ({
      key: `m:${item}`,
      label: labels.get(`m:${item}`) ?? item,
      clear: () =>
        update({
          manufacturers: value.manufacturers.filter((name) => name !== item),
        }),
    })),
    ...value.categories.map((item) => ({
      key: `c:${item}`,
      label: labels.get(`c:${item}`) ?? item,
      clear: () =>
        update({
          categories: value.categories.filter((name) => name !== item),
        }),
    })),
    ...value.facets.map((item) => ({
      key: item,
      label: specLabel(item),
      clear: () => update({ facets: value.facets.filter((facet) => facet !== item) }),
    })),
    ...(priceChip(value.minPrice, value.maxPrice)
      ? [
          {
            key: 'price',
            label: priceChip(value.minPrice, value.maxPrice)!,
            clear: () => update({ minPrice: '', maxPrice: '' }),
          },
        ]
      : []),
  ]
  const categories = available?.taxonomy.find((group) => group.key === 'category')

  return (
    <div className={cn('min-w-0 space-y-4', className)}>
      {active.length ? (
        <div aria-label="Active filters" className="flex flex-wrap gap-1.5">
          {active.map((item) => (
            <button
              aria-label={`Remove filter ${item.label}`}
              className="inline-flex min-h-7 items-center gap-1 rounded-full border bg-background px-2.5 text-xs font-medium hover:bg-muted"
              key={item.key}
              onClick={item.clear}
              type="button"
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">{item.label}</span>
              <X aria-hidden="true" className="size-3 shrink-0" />
            </button>
          ))}
        </div>
      ) : null}
      <PriceRange
        max={value.maxPrice}
        min={value.minPrice}
        onChange={(minPrice, maxPrice) => update({ minPrice, maxPrice })}
      />
      <CheckboxGroup
        label="Manufacturer"
        onToggle={(option, label) => {
          remember(`m:${option}`, label)
          update({ manufacturers: toggle(value.manufacturers, option) })
        }}
        options={available?.manufacturers ?? []}
        selected={value.manufacturers}
      />
      {categories ? (
        <CheckboxGroup
          collapsible
          defaultOpen
          label={categories.label}
          onToggle={(option, label) => {
            remember(`c:${option}`, label)
            update({ categories: toggle(value.categories, option) })
          }}
          options={categories.options}
          selected={value.categories}
        />
      ) : null}
      {available?.specifications.map((group, index) => (
        <CheckboxGroup
          collapsible
          defaultOpen={index < 3}
          key={group.key}
          label={group.label}
          onToggle={(option, label) => {
            const facet = `${group.key}=${option}`
            remember(facet, label)
            update({ facets: toggle(value.facets, facet) })
          }}
          options={group.options}
          selected={value.facets
            .filter((facet) => facet.startsWith(`${group.key}=`))
            .map((facet) => facet.slice(group.key.length + 1))}
        />
      ))}
      {active.length ? (
        <button
          className="text-sm font-medium underline-offset-2 hover:underline"
          onClick={() => onChange(emptyProductFilters)}
          type="button"
        >
          Clear filters
        </button>
      ) : null}
    </div>
  )
}
